import { IApiBaseProps, IResultResponse, IUseFetchOptionsParams } from '@common/api.models';
import { isValidApiBaseProps } from '@common/api.services';
import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import { AxiosErrorResponse } from '@core/error.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { IItemResponse, IListResponse, IPagedResultsV1 } from '@gcl/react-core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createUseFetchOptionsWithSearchFn } from '@common/selectOptions.services';
import {
  getProjectUserProfileOptionsByCompanyAsync,
  IGetProjectUserProfileOptionsByCompanyProps,
  getAllProjectUserProfilesAsync,
  getProjectUserProfileAsync,
  IGetProjectUserProfileProps,
  updateProjectUserProfileAsync,
  createProjectUserProfileAsync,
  IRemoveUserFromCompanyProps,
  removeUserFromCompanyAsync,
  ILockUnlockProjectUserProfileProps,
  unlockProjectUserProfileAsync,
  lockProjectUserProfileAsync,
  getProjectUserProfileOptionsWithSearchAsync,
} from './projectUserProfile.services';
import {
  IProjectUserProfile,
  IProjectUserProfileOption,
  ICreateProjectUserProfileRequest,
  IUpdateProjectUserProfileRequest,
} from './projectUserProfile.models';

export const useGetAllProjectUserProfilesAsync = (props: IApiBaseProps, enabled = true) => {
  return useQuery<IPagedResultsV1<IProjectUserProfile>, AxiosErrorResponse>(
    [props.projectId, FETCH_QUERIES_NAME.ALL_PROJECT_USER_PROFILES],
    async () => {
      const result = await getAllProjectUserProfilesAsync(props);
      return result;
    },
    { enabled: isValidApiBaseProps(props) && enabled },
  );
};

export const useGetProjectUserProfileAsync = ({ id, ...props }: IGetProjectUserProfileProps, enabled = true) => {
  return useQuery<IListResponse<IProjectUserProfile>, AxiosErrorResponse>(
    [props.projectId, FETCH_QUERIES_NAME.PROJECT_USER_PROFILE, id],
    async () => {
      const result = await getProjectUserProfileAsync({ id, ...props });
      return result;
    },
    { enabled: isPositiveNumber(id) && isValidApiBaseProps(props) && enabled },
  );
};

export const useGetProjectUserProfileOptionsByCompanyAsync = (
  { companyId, ...props }: IGetProjectUserProfileOptionsByCompanyProps,
  enabled = true,
) => {
  return useQuery<IPagedResultsV1<IProjectUserProfileOption>, AxiosErrorResponse>(
    [props.projectId, companyId, FETCH_QUERIES_NAME.PROJECT_USER_PROFILE_OPTIONS],
    async () => {
      const result = await getProjectUserProfileOptionsByCompanyAsync({ companyId, ...props });
      return result;
    },
    { enabled: isPositiveNumber(companyId) && isValidApiBaseProps(props) && enabled },
  );
};

export const useCreateProjectUserProfileAsync = () => {
  return useMutation<IItemResponse<IProjectUserProfile>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.CREATE_PROJECT_USER_PROFILE],
    async (createRequest: ICreateProjectUserProfileRequest) => {
      const result = await createProjectUserProfileAsync(createRequest);
      return result;
    },
  );
};

export const useUpdateProjectUserProfileAsync = () => {
  return useMutation<IItemResponse<IProjectUserProfile>, AxiosErrorResponse, IUpdateProjectUserProfileRequest, unknown>(
    [MUTATION_KEYS.UPDATE_PROJECT_USER_PROFILE],
    async (updateRequest: IUpdateProjectUserProfileRequest) => {
      const result = await updateProjectUserProfileAsync(updateRequest);
      return result;
    },
  );
};

export const useRemoveUserFromCompanyAsync = () => {
  return useMutation<IResultResponse, AxiosErrorResponse, IRemoveUserFromCompanyProps, unknown>(
    [MUTATION_KEYS.REMOVE_USER_FROM_COMPANY],
    async (deleteProps: IRemoveUserFromCompanyProps) => {
      const result = await removeUserFromCompanyAsync(deleteProps);
      return result;
    },
  );
};

export const useLockProjectUserProfileAsync = () => {
  return useMutation<IResultResponse, AxiosErrorResponse, ILockUnlockProjectUserProfileProps, unknown>(
    [MUTATION_KEYS.LOCK_PROJECT_USER_PROFILE],
    async (props: ILockUnlockProjectUserProfileProps) => {
      const result = await lockProjectUserProfileAsync(props);
      return result;
    },
  );
};

export const useUnlockProjectUserProfileAsync = () => {
  return useMutation<IResultResponse, AxiosErrorResponse, ILockUnlockProjectUserProfileProps, unknown>(
    [MUTATION_KEYS.UNLOCK_PROJECT_USER_PROFILE],
    async (props: ILockUnlockProjectUserProfileProps) => {
      const result = await unlockProjectUserProfileAsync(props);
      return result;
    },
  );
};

export const useFetchProjectUserProfileOptionsWithSearch = ({
  isDirty = false,
  shouldConvertOptions = false,
}: IUseFetchOptionsParams) =>
  createUseFetchOptionsWithSearchFn({
    queryName: FETCH_QUERIES_NAME.SEARCH_PROJECT_USER_PROFILE_OPTIONS,
    getOptionsWithSearchFn: getProjectUserProfileOptionsWithSearchAsync,
    handleOptionsProps: { shouldConvertOptions, idFieldName: 'id', textFieldName: 'displayName' },
    enabled: !isDirty,
  });
